(function () {
  'use strict';

  const QUOTE_ENDPOINT = '/apps/gst-verify/server-quote';
  const CATALOG_ENDPOINT = '/apps/gst-verify/server-platforms';

  const WORKLOAD_PRESETS = {
    Virtualization: {
      cpu: 'Intel Xeon Gold',
      ram: '128 GB',
      storage: '2x 960GB NVMe',
      raid: 'RAID 1',
      gpu: 'None',
    },
    'AI / GPU compute': {
      cpu: 'AMD EPYC',
      ram: '256 GB',
      storage: '4x 1.92TB SSD',
      raid: 'RAID 10',
      gpu: 'GPU-ready chassis',
    },
    'SQL Database': {
      cpu: 'Intel Xeon Gold',
      ram: '256 GB',
      storage: '2x 960GB NVMe',
      raid: 'RAID 1',
      gpu: 'None',
    },
    'File Server': {
      cpu: 'Intel Xeon Silver',
      ram: '64 GB',
      storage: 'Custom mix',
      raid: 'RAID 6',
      gpu: 'None',
    },
    Backup: {
      cpu: 'Intel Xeon Silver',
      ram: '64 GB',
      storage: 'Custom mix',
      raid: 'RAID 6',
      gpu: 'None',
    },
    'Private Cloud': {
      cpu: 'AMD EPYC',
      ram: '128 GB',
      storage: '4x 1.92TB SSD',
      raid: 'RAID 10',
      gpu: 'None',
    },
  };

  const DEFAULT_PLATFORMS = [
    { id: 'dell-r760', brand: 'Dell', title: 'PowerEdge R760', formFactor: '2U Rack', tags: ['Intel Xeon Gen5', 'Up to 8TB DDR5 RAM'] },
    { id: 'dell-r740', brand: 'Dell', title: 'PowerEdge R740', formFactor: '2U Rack', tags: ['Balanced compute', 'Flexible bays'] },
    { id: 'hpe-dl380-gen11', brand: 'HPE', title: 'ProLiant DL380 Gen11', formFactor: '2U Rack', tags: ['24x7 workload', 'Scale-ready'] },
    { id: 'hpe-dl360-gen12', brand: 'HPE', title: 'ProLiant DL360 Gen12', formFactor: '1U Rack', tags: ['Space efficient', 'High uptime'] },
    { id: 'hpe-dl345-gen11', brand: 'HPE', title: 'ProLiant DL345 Gen11', formFactor: '1U Rack', tags: ['AMD EPYC', 'Efficient'] },
    { id: 'dell-t430', brand: 'Dell', title: 'PowerEdge T430', formFactor: 'Tower', tags: ['SMB', 'Office-friendly'] },
  ];

  const OPTION_FIELD_MAP = {
    workload: '[data-sq-workload]',
    cpu: '[data-sq-cpu]',
    ram: '[data-sq-ram]',
    storage: '[data-sq-storage]',
    warranty: '[data-sq-warranty]',
  };

  function $(root, sel) {
    return root.querySelector(sel);
  }

  function setStatus(root, message, tone) {
    root.querySelectorAll('[data-sq-status]').forEach((el) => {
      el.hidden = !message;
      el.textContent = message || '';
      el.dataset.tone = tone || 'info';
    });
  }

  function readInlinePlatforms(root) {
    const raw = root.getAttribute('data-platforms');
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) && parsed.length ? parsed : [];
    } catch (_) {
      return [];
    }
  }

  function normalizePlatform(platform) {
    if (!platform) return null;
    const id = platform.id || platform.platformKey;
    if (!id) return null;
    return {
      id,
      brand: platform.brand || '',
      title: platform.title || id,
      formFactor: platform.formFactor || '',
      badge: platform.badge || '',
      imageUrl: platform.imageUrl || platform.image_url || '',
      tags: platform.tags || platform.highlights || [],
      useCases: platform.useCases || [],
      defaults: platform.defaults || {},
      options: platform.options || null,
      product: platform.product || null,
      rules: platform.rules || [],
    };
  }

  function resolveInitialPlatforms(root) {
    const inline = readInlinePlatforms(root).map(normalizePlatform).filter(Boolean);
    if (inline.length) return inline;
    return DEFAULT_PLATFORMS.map(normalizePlatform);
  }

  function populatePlatformSelect(root, platforms) {
    const select = $(root, '[data-sq-platform]');
    if (!select) return;
    const currentValue = select.value;
    select.innerHTML = '';
    platforms.forEach((platform) => {
      const option = document.createElement('option');
      option.value = platform.id;
      const suffix = platform.formFactor ? ` — ${platform.formFactor}` : '';
      option.textContent = `${platform.title || platform.id}${suffix}`.trim();
      select.appendChild(option);
    });
    if (currentValue && platforms.some((p) => p.id === currentValue)) {
      select.value = currentValue;
    }
  }

  function populateOptionSelect(root, selector, rows, { includeBlank } = {}) {
    const select = $(root, selector);
    if (!select || !Array.isArray(rows) || !rows.length) return;
    const currentValue = select.value;
    select.innerHTML = '';
    if (includeBlank) {
      const blank = document.createElement('option');
      blank.value = '';
      blank.textContent = 'Select workload';
      select.appendChild(blank);
    }
    let defaultValue = '';
    rows.forEach((row) => {
      const option = document.createElement('option');
      option.value = row.value;
      option.textContent = row.label || row.value;
      select.appendChild(option);
      if (row.isDefault) defaultValue = row.value;
    });
    if (currentValue && rows.some((row) => row.value === currentValue)) {
      select.value = currentValue;
    } else if (defaultValue) {
      select.value = defaultValue;
    }
  }

  function populateOptionGroups(root, options) {
    if (!options || typeof options !== 'object') return;
    Object.entries(OPTION_FIELD_MAP).forEach(([group, selector]) => {
      populateOptionSelect(root, selector, options[group] || [], {
        includeBlank: group === 'workload',
      });
    });
  }

  function applyOptionsForPlatform(root, state, platformId) {
    const platform = findPlatform(state.platforms, platformId);
    const options = platform?.options || state.options;
    if (options) populateOptionGroups(root, options);
  }

  function getOptionsForPlatform(state, platform) {
    return platform?.options || state.options || {};
  }

  function formatMoney(amount, currencyCode) {
    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: currencyCode || 'INR',
        maximumFractionDigits: 0,
      }).format(amount);
    } catch (_) {
      return `₹${Math.round(amount).toLocaleString('en-IN')}`;
    }
  }

  function updatePriceStatus(root, state) {
    const platform = findPlatform(state.platforms, $(root, '[data-sq-platform]')?.value);
    const product = platform?.product;
    const wrapper = $(root, '[data-sq-price-status]');
    if (!wrapper || product?.priceMin == null) {
      if (wrapper) wrapper.hidden = true;
      return;
    }

    let min = Number(product.priceMin) || 0;
    let max = Number(product.priceMax ?? product.priceMin) || min;
    const options = getOptionsForPlatform(state, platform);
    Object.entries(OPTION_FIELD_MAP).forEach(([group, selector]) => {
      if (group === 'workload') return;
      const value = $(root, selector)?.value;
      const row = (options[group] || []).find((option) => option.value === value);
      if (!row) return;
      min += Number(row.priceDeltaMin) || 0;
      max += Number(row.priceDeltaMax ?? row.priceDeltaMin) || 0;
    });

    const quantity = Math.max(1, parseInt($(root, '[data-sq-quantity]')?.value || '1', 10) || 1);
    min *= quantity;
    max *= quantity;
    const currency = product.currencyCode || 'INR';
    root.dataset.sqEstimateMin = String(min);
    root.dataset.sqEstimateMax = String(max);
    root.dataset.sqEstimateCurrency = currency;
    const valueNode = $(root, '[data-sq-price-value]');
    const labelNode = $(root, '[data-sq-price-label]');
    if (labelNode) {
      labelNode.textContent = min === max ? 'Starting from' : 'Estimated range';
    }
    if (valueNode) {
      valueNode.textContent =
        min === max
          ? formatMoney(min, currency)
          : `${formatMoney(min, currency)} – ${formatMoney(max, currency)}`;
    }
    wrapper.hidden = false;
  }

  function applyCompatibilityRules(root, state) {
    const platform = findPlatform(state.platforms, $(root, '[data-sq-platform]')?.value);
    const rules = Array.isArray(platform?.rules) ? platform.rules : [];
    const messages = [];

    Object.values(OPTION_FIELD_MAP).forEach((selector) => {
      const select = $(root, selector);
      Array.from(select?.options || []).forEach((option) => {
        option.disabled = false;
      });
    });

    rules
      .filter((rule) => rule.active !== false && rule.effect === 'DISABLE')
      .forEach((rule) => {
        const conditionSelect = $(root, OPTION_FIELD_MAP[rule.conditionGroup]);
        if (!conditionSelect || conditionSelect.value !== rule.conditionValue) return;
        const targetSelect = $(root, OPTION_FIELD_MAP[rule.targetGroup]);
        const target = Array.from(targetSelect?.options || []).find(
          (option) => option.value === rule.targetValue,
        );
        if (!target) return;
        target.disabled = true;
        if (targetSelect.value === target.value) {
          const replacement = Array.from(targetSelect.options).find(
            (option) => !option.disabled && option.value,
          );
          targetSelect.value = replacement?.value || '';
        }
        if (rule.message) messages.push(rule.message);
      });

    const messageNode = $(root, '[data-sq-rule-message]');
    if (messageNode) {
      const unique = [...new Set(messages)];
      messageNode.textContent = unique.join(' ');
      messageNode.hidden = unique.length === 0;
    }
    updateSummary(root, state.platforms);
    updatePriceStatus(root, state);
  }

  async function fetchCatalog() {
    try {
      const response = await fetch(CATALOG_ENDPOINT, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) return null;
      const payload = await response.json();
      if (!payload?.success || !Array.isArray(payload.platforms) || !payload.platforms.length) {
        return null;
      }
      return {
        platforms: payload.platforms.map(normalizePlatform).filter(Boolean),
        options: payload.options || {},
        source: payload.source || 'app',
      };
    } catch (_) {
      return null;
    }
  }

  function findPlatform(platforms, id) {
    return platforms.find((p) => p.id === id) || platforms[0];
  }

  function getHubScope(root) {
    return root.closest('.server-hub, .server-product') || document;
  }

  function getPlatformCards(root) {
    return getHubScope(root).querySelectorAll('[data-sq-platform-card]');
  }

  function formatSummaryValue(value, fallback) {
    if (!value || value === '—') return fallback || 'Not selected';
    return value;
  }

  function updateSummary(root, platforms) {
    const platformId = $(root, '[data-sq-platform]')?.value;
    const platform = findPlatform(platforms, platformId);
    const platformTitle = root.querySelector('[data-sq-summary-platform-title]');
    if (platformTitle) {
      platformTitle.textContent = platform ? platform.title : 'Select a platform';
    }

    const media = root.querySelector('[data-sq-summary-media]');
    const image = root.querySelector('[data-sq-summary-image]');
    const imageUrl =
      platform?.imageUrl ||
      getHubScope(root)
        .querySelector(`[data-sq-platform-card][data-platform-id="${CSS.escape(platformId || '')}"]`)
        ?.getAttribute('data-platform-image') ||
      '';
    if (media && image) {
      if (imageUrl) {
        image.src = imageUrl;
        image.alt = platform?.title || 'Selected platform';
        media.hidden = false;
        media.classList.add('is-visible');
      } else {
        image.removeAttribute('src');
        image.alt = '';
        media.hidden = true;
        media.classList.remove('is-visible');
      }
    }

    const map = {
      workload: formatSummaryValue(
        (() => {
          const workload = $(root, '[data-sq-workload]')?.value || '';
          if (workload === 'Other') {
            return $(root, '[data-sq-workload-custom]')?.value?.trim() || 'Other / custom';
          }
          return workload;
        })(),
      ),
      cpu: formatSummaryValue($(root, '[data-sq-cpu]')?.value),
      ram: formatSummaryValue($(root, '[data-sq-ram]')?.value),
      storage: formatSummaryValue($(root, '[data-sq-storage]')?.value),
      raid: $(root, '[data-sq-raid]')?.value || 'RAID 10',
      warranty: formatSummaryValue($(root, '[data-sq-warranty]')?.value),
    };
    Object.entries(map).forEach(([key, value]) => {
      const node = root.querySelector(`[data-sq-summary-${key}]`);
      if (node) node.textContent = value;
    });
  }

  function applyPlatformPreset(root, platformId, platforms) {
    const platform = findPlatform(platforms, platformId);
    const preset = platform?.defaults || {};
    if (preset.cpu) setFieldValue(root, '[data-sq-cpu]', preset.cpu);
    if (preset.ram) setFieldValue(root, '[data-sq-ram]', preset.ram);
    if (preset.storage) setFieldValue(root, '[data-sq-storage]', preset.storage);
    if (preset.warranty) setFieldValue(root, '[data-sq-warranty]', preset.warranty);
  }

  function selectPlatform(root, id, platforms, options = {}, state = null) {
    if (!id) return;

    getPlatformCards(root).forEach((card) => {
      const selected = card.getAttribute('data-platform-id') === id;
      card.classList.toggle('is-selected', selected);
      card.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    const select = $(root, '[data-sq-platform]');
    if (select && select.value !== id) select.value = id;

    if (state) {
      applyOptionsForPlatform(root, state, id);
      // Platform change reshapes available options — re-apply chassis defaults.
      applyPlatformPreset(root, id, platforms);
      const workloadSelect = $(root, '[data-sq-workload]');
      if (workloadSelect) {
        const allowed = Array.from(workloadSelect.options).map((o) => o.value);
        if (!allowed.includes(workloadSelect.value)) {
          workloadSelect.value = '';
        }
      }
    } else {
      const workload = $(root, '[data-sq-workload]')?.value;
      if (!workload) applyPlatformPreset(root, id, platforms);
    }

    updateSummary(root, platforms);
    if (state) applyCompatibilityRules(root, state);

    if (options.scroll) {
      document.getElementById('server-quote-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    if (options.openStep !== false) {
      showStep(root, 0);
    }
  }

  function setFieldValue(root, selector, value) {
    const el = $(root, selector);
    if (!el || value === undefined || value === null) return;
    if (el.tagName === 'SELECT') {
      const options = Array.from(el.options || []);
      const match = options.find((option) => option.value === value);
      if (match) el.value = value;
      return;
    }
    el.value = value;
  }

  function applyWorkloadPreset(root) {
    const workload = $(root, '[data-sq-workload]')?.value;
    if (!workload || !WORKLOAD_PRESETS[workload]) return;
    const preset = WORKLOAD_PRESETS[workload];
    setFieldValue(root, '[data-sq-cpu]', preset.cpu);
    setFieldValue(root, '[data-sq-ram]', preset.ram);
    setFieldValue(root, '[data-sq-storage]', preset.storage);
    setFieldValue(root, '[data-sq-raid]', preset.raid);
    setFieldValue(root, '[data-sq-gpu]', preset.gpu);
  }

  function syncWorkloadCustomVisibility(root) {
    const wrap = $(root, '[data-sq-workload-custom-wrap]');
    const custom = $(root, '[data-sq-workload-custom]');
    const workload = $(root, '[data-sq-workload]')?.value || '';
    const isOther = workload === 'Other';
    if (wrap) wrap.hidden = !isOther;
    if (custom) {
      custom.required = isOther;
      if (!isOther) custom.value = '';
    }
  }

  function buildCombinedNotes(root) {
    const parts = [];
    const workload = $(root, '[data-sq-workload]')?.value || '';
    const workloadCustom = $(root, '[data-sq-workload-custom]')?.value?.trim() || '';
    const workloadNotes = $(root, '[data-sq-workload-notes]')?.value?.trim() || '';
    const hardwareNotes = $(root, '[data-sq-hardware-notes]')?.value?.trim() || '';
    const notes = $(root, '[data-sq-notes]')?.value?.trim() || '';

    if (workload === 'Other' && workloadCustom) {
      parts.push(`Custom workload: ${workloadCustom}`);
    }
    if (workloadNotes) parts.push(`Workload notes: ${workloadNotes}`);
    if (hardwareNotes) parts.push(`Hardware requirements: ${hardwareNotes}`);
    if (notes) parts.push(notes);
    return parts.join('\n\n').slice(0, 2000);
  }

  function validateConfigurationSteps(root, stepIndex) {
    if (stepIndex <= 1) {
      const workload = $(root, '[data-sq-workload]')?.value || '';
      if (!workload) {
        setStatus(root, 'Select a primary workload to continue.', 'error');
        return false;
      }
      if (workload === 'Other') {
        const custom = $(root, '[data-sq-workload-custom]')?.value?.trim() || '';
        if (custom.length < 4) {
          setStatus(root, 'Please describe your custom workload (at least a few words).', 'error');
          $(root, '[data-sq-workload-custom]')?.focus();
          return false;
        }
      }
    }
    setStatus(root, '', '');
    return true;
  }

  function setProgressStep(root, stepIndex) {
    root.querySelectorAll('[data-sq-progress-step]').forEach((node, index) => {
      node.classList.toggle('is-active', index === stepIndex);
      node.classList.toggle('is-done', index < stepIndex);
    });
  }

  function showStep(root, stepIndex) {
    const items = Array.from(root.querySelectorAll('.sq-accordion__item'));
    const maxStep = items.length - 1;
    const clamped = Math.min(Math.max(stepIndex, 0), maxStep);

    items.forEach((item, index) => {
      item.classList.toggle('is-active', index === clamped);
    });

    setProgressStep(root, clamped);
    root.dataset.sqStep = String(clamped);
  }

  function bindAccordion(root) {
    root.querySelectorAll('[data-sq-accordion-toggle]').forEach((toggle) => {
      toggle.addEventListener('click', () => {
        const index = parseInt(toggle.getAttribute('data-sq-accordion-toggle') || '0', 10);
        showStep(root, index);
      });
    });

    root.querySelectorAll('[data-sq-continue]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const index = parseInt(btn.getAttribute('data-sq-continue') || '1', 10);
        const current = parseInt(root.dataset.sqStep || '0', 10);
        if (index > current && !validateConfigurationSteps(root, current)) return;
        showStep(root, index);
      });
    });

    root.querySelectorAll('[data-sq-progress-step]').forEach((pill) => {
      pill.addEventListener('click', (event) => {
        event.preventDefault();
        const index = parseInt(pill.getAttribute('data-sq-progress-step') || '0', 10);
        showStep(root, index);
      });
    });

    $(root, '[data-sq-change-platform]')?.addEventListener('click', () => {
      showStep(root, 0);
      document.getElementById('server-quote-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    showStep(root, 0);
  }

  function bindQuantitySync(root, state) {
    const hidden = $(root, '[data-sq-quantity]');
    const visible = $(root, '[data-sq-qty-visible]');
    if (!hidden || !visible) return;

    const sync = (value, persist = true) => {
      const qty = Math.max(1, parseInt(value || '1', 10) || 1);
      hidden.value = String(qty);
      visible.value = String(qty);
      if (state) updatePriceStatus(root, state);
      if (state && persist) saveDraft(root, state);
    };

    visible.addEventListener('input', () => sync(visible.value));
    visible.addEventListener('change', () => sync(visible.value));
    sync(hidden.value, false);
  }

  function bindPlatformCards(root, state) {
    getPlatformCards(root).forEach((card) => {
      if (card.dataset.sqCardBound === '1') return;
      card.dataset.sqCardBound = '1';
      card.setAttribute('role', 'button');
      card.setAttribute('tabindex', '0');
      card.setAttribute('aria-pressed', 'false');

      const activate = (event) => {
        if (event?.target?.closest('a')) return;
        const id = card.getAttribute('data-platform-id');
        selectPlatform(root, id, state.platforms, { scroll: true }, state);
        saveDraft(root, state, { platformKey: id });
        if (root.getAttribute('data-logged-in') !== 'true') {
          const loginUrl = root.getAttribute('data-login-url');
          if (loginUrl) window.location.href = loginUrl;
        }
      };

      card.addEventListener('click', activate);
      card.addEventListener('keydown', (event) => {
        if (event.target.closest('a')) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          activate(event);
        }
      });
    });
  }

  function collectPayload(root, platforms) {
    const platformId = $(root, '[data-sq-platform]')?.value;
    const platform = findPlatform(platforms, platformId);
    return {
      source: root.getAttribute('data-source') || 'website',
      platform: platform?.title || '',
      platformKey: platform?.id || '',
      brand: platform?.brand || '',
      formFactor: platform?.formFactor || '',
      shopifyProductId: platform?.product?.id || '',
      sku: platform?.product?.sku || '',
      catalogPriceMin: platform?.product?.priceMin ?? null,
      catalogPriceMax: platform?.product?.priceMax ?? null,
      estimatedPriceMin: Number(root.dataset.sqEstimateMin) || null,
      estimatedPriceMax: Number(root.dataset.sqEstimateMax) || null,
      currencyCode: root.dataset.sqEstimateCurrency || platform?.product?.currencyCode || 'INR',
      inventoryQty: platform?.product?.inventoryQty ?? null,
      workload: $(root, '[data-sq-workload]')?.value || '',
      cpu: $(root, '[data-sq-cpu]')?.value || '',
      ram: $(root, '[data-sq-ram]')?.value || '',
      storage: $(root, '[data-sq-storage]')?.value || '',
      raid: $(root, '[data-sq-raid]')?.value || '',
      nic: $(root, '[data-sq-nic]')?.value || '',
      gpu: $(root, '[data-sq-gpu]')?.value || '',
      os: $(root, '[data-sq-os]')?.value || '',
      warranty: $(root, '[data-sq-warranty]')?.value || '',
      quantity: parseInt($(root, '[data-sq-quantity]')?.value || '1', 10) || 1,
      notes: buildCombinedNotes(root),
      workloadCustom: $(root, '[data-sq-workload-custom]')?.value?.trim() || '',
      workloadNotes: $(root, '[data-sq-workload-notes]')?.value?.trim() || '',
      hardwareNotes: $(root, '[data-sq-hardware-notes]')?.value?.trim() || '',
      contactNotes: $(root, '[data-sq-notes]')?.value?.trim() || '',
      name: $(root, '[data-sq-name]')?.value?.trim() || '',
      company: $(root, '[data-sq-company]')?.value?.trim() || '',
      email: $(root, '[data-sq-email]')?.value?.trim() || '',
      phone: $(root, '[data-sq-phone]')?.value?.trim() || '',
      city: $(root, '[data-sq-city]')?.value?.trim() || '',
      gstin: $(root, '[data-sq-gstin]')?.value?.trim() || '',
      productHandle: root.getAttribute('data-product-handle') || '',
      productTitle: root.getAttribute('data-product-title') || '',
      productUrl: root.getAttribute('data-product-url') || '',
      consent: true,
    };
  }

  async function submitForm(root, state) {
    if (root.getAttribute('data-logged-in') !== 'true') {
      saveDraft(root, state);
      setStatus(
        root,
        'Create an account or log in to submit — we’ll restore your configuration.',
        'error',
      );
      const registerUrl = root.getAttribute('data-register-url');
      if (registerUrl) window.location.href = registerUrl;
      return;
    }

    if (!validateConfigurationSteps(root, 1)) {
      showStep(root, 1);
      return;
    }

    const submitButtons = root.querySelectorAll('[data-sq-submit]');
    submitButtons.forEach((submit) => {
      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
    });
    setStatus(root, 'Sending your quote request…', 'info');

    try {
      const response = await fetch(QUOTE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(collectPayload(root, state.platforms)),
      });
      let payload = {};
      try {
        payload = await response.json();
      } catch (_) {
        payload = {};
      }
      if (payload.code === 'LOGIN_REQUIRED' || response.status === 401) {
        saveDraft(root, state);
        setStatus(
          root,
          payload.message || 'Please log in to submit and track your quote.',
          'error',
        );
        const registerUrl = root.getAttribute('data-register-url');
        if (registerUrl) window.location.href = registerUrl;
        return;
      }
      if (!response.ok && !payload.message) {
        setStatus(
          root,
          'Could not send your quote request. Please try again or email sales@blueinfosol.in.',
          'error',
        );
        submitButtons.forEach((submit) => {
          submit.disabled = false;
        });
        return;
      }
      if (payload.success) {
        clearDraft();
        const quotesUrl =
          root.getAttribute('data-account-url') ||
          '/pages/my-server-quotes';
        setStatus(
          root,
          (payload.message || 'Quote request sent.') +
            ' Track status anytime on My Server Quotes.',
          'success',
        );
        submitButtons.forEach((submit) => {
          submit.textContent = 'Quote sent';
        });
        const track = document.createElement('p');
        track.className = 'server-quote-form__secure';
        track.innerHTML =
          '<a href="' +
          quotesUrl.replace(/"/g, '&quot;') +
          '">Open My Server Quotes</a>';
        const statusEl = $(root, '[data-sq-status]');
        if (statusEl?.parentNode) statusEl.parentNode.appendChild(track);
        return;
      }
      setStatus(
        root,
        payload.message ||
          'Could not send your quote request. Please try again or email sales@blueinfosol.in.',
        'error',
      );
      submitButtons.forEach((submit) => {
        submit.disabled = false;
      });
    } catch (_) {
      setStatus(root, 'Network error. Please try again.', 'error');
      submitButtons.forEach((submit) => {
        submit.disabled = false;
      });
    } finally {
      submitButtons.forEach((submit) => {
        submit.removeAttribute('aria-busy');
      });
    }
  }

  const DRAFT_KEY = 'blueberry_sq_draft_v2';
  const DRAFT_KEY_LEGACY = 'blueberry_sq_draft_v1';

  function writeDraft(payload) {
    try {
      const serialized = JSON.stringify({
        ...payload,
        savedAt: Date.now(),
        path: `${window.location.pathname}${window.location.search}`,
      });
      try {
        localStorage.setItem(DRAFT_KEY, serialized);
        localStorage.removeItem(DRAFT_KEY_LEGACY);
      } catch (_) {
        sessionStorage.setItem(DRAFT_KEY, serialized);
        try {
          sessionStorage.removeItem(DRAFT_KEY_LEGACY);
        } catch (__) {}
      }
    } catch (_) {}
  }

  function saveDraft(root, state, extras = {}) {
    try {
      if ($(root, '[data-sq-form]')) {
        writeDraft({
          ...collectPayload(root, state.platforms),
          step: parseInt(root.dataset.sqStep || '0', 10) || 0,
          pendingLogin: false,
          ...extras,
        });
        return;
      }
      // Logged-out hard gate: keep at least the chosen platform card.
      const existing = readDraft() || {};
      writeDraft({
        ...existing,
        source: root.getAttribute('data-source') || existing.source || 'website',
        platformKey:
          extras.platformKey ||
          existing.platformKey ||
          root.getAttribute('data-preset-platform') ||
          '',
        quantity: existing.quantity || 1,
        pendingLogin: true,
        step: 0,
        ...extras,
      });
    } catch (_) {}
  }

  function clearDraft() {
    try {
      localStorage.removeItem(DRAFT_KEY);
      localStorage.removeItem(DRAFT_KEY_LEGACY);
    } catch (_) {}
    try {
      sessionStorage.removeItem(DRAFT_KEY);
      sessionStorage.removeItem(DRAFT_KEY_LEGACY);
    } catch (_) {}
  }

  function readDraft() {
    try {
      let raw = null;
      try {
        raw = localStorage.getItem(DRAFT_KEY) || localStorage.getItem(DRAFT_KEY_LEGACY);
      } catch (_) {}
      if (!raw) {
        try {
          raw = sessionStorage.getItem(DRAFT_KEY) || sessionStorage.getItem(DRAFT_KEY_LEGACY);
        } catch (__) {}
      }
      if (!raw) return null;
      const draft = JSON.parse(raw);
      if (!draft || Date.now() - (draft.savedAt || 0) > 1000 * 60 * 60 * 24) {
        clearDraft();
        return null;
      }
      return draft;
    } catch (_) {
      return null;
    }
  }

  function applyDraft(root, state, draft) {
    if (!draft) return;
    const map = [
      ['[data-sq-workload]', draft.workload],
      ['[data-sq-cpu]', draft.cpu],
      ['[data-sq-ram]', draft.ram],
      ['[data-sq-storage]', draft.storage],
      ['[data-sq-raid]', draft.raid],
      ['[data-sq-nic]', draft.nic],
      ['[data-sq-gpu]', draft.gpu],
      ['[data-sq-os]', draft.os],
      ['[data-sq-warranty]', draft.warranty],
      ['[data-sq-quantity]', draft.quantity],
      ['[data-sq-qty-visible]', draft.quantity],
      ['[data-sq-workload-custom]', draft.workloadCustom],
      ['[data-sq-workload-notes]', draft.workloadNotes],
      ['[data-sq-hardware-notes]', draft.hardwareNotes],
      ['[data-sq-notes]', draft.contactNotes || draft.notes],
      ['[data-sq-name]', draft.name],
      ['[data-sq-company]', draft.company],
      ['[data-sq-phone]', draft.phone],
      ['[data-sq-city]', draft.city],
      ['[data-sq-gstin]', draft.gstin],
    ];
    map.forEach(([sel, value]) => {
      const el = $(root, sel);
      if (el && value != null && value !== '') el.value = value;
    });
    syncWorkloadCustomVisibility(root);
    // Keep account email when logged in
    const emailEl = $(root, '[data-sq-email]');
    if (emailEl && !emailEl.readOnly && draft.email) {
      emailEl.value = draft.email;
    }
    if (draft.platformKey) {
      selectPlatform(root, draft.platformKey, state.platforms, {
        scroll: false,
        openStep: false,
      }, state);
    }
    updateSummary(root, state.platforms);
    updatePriceStatus(root, state);

    // After login: open contact step. Normal refresh: stay on saved step (default platform).
    const resumeStep = draft.pendingLogin
      ? 3
      : Math.min(Math.max(parseInt(draft.step, 10) || 0, 0), 3);
    if (draft.pendingLogin) {
      setStatus(root, 'Configuration restored — review and submit your quote.', 'info');
      writeDraft({ ...draft, pendingLogin: false, step: resumeStep });
    }
    showStep(root, resumeStep);
  }

  function bindAccountGate(root, state) {
    root.querySelectorAll('[data-sq-save-and-go]').forEach((link) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        saveDraft(root, state);
        window.location.href = link.href;
      });
    });
  }

  function applyInitialSelection(root, state) {
    const platformSelect = $(root, '[data-sq-platform]');
    const preset = root.getAttribute('data-preset-platform');
    const initialPlatform =
      preset || platformSelect?.value || state.platforms[0]?.id || DEFAULT_PLATFORMS[0].id;
    selectPlatform(root, initialPlatform, state.platforms, { scroll: false, openStep: false }, state);
  }

  const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

  function setGstinStatus(root, message, tone) {
    const el = $(root, '[data-sq-gstin-status]');
    if (!el) return;
    el.textContent = message || '';
    el.dataset.tone = tone || '';
    el.hidden = !message;
  }

  function bindGstinVerify(root) {
    const gstinInput = $(root, '[data-sq-gstin]');
    const companyInput = $(root, '[data-sq-company]');
    if (!gstinInput || !companyInput) return;

    let lookupTimeout = null;
    let verifiedFromGstin = false;
    window.__gstVerifyCache = window.__gstVerifyCache || {};

    function unlockCompany() {
      if (verifiedFromGstin) {
        companyInput.readOnly = false;
        verifiedFromGstin = false;
      }
    }

    function applyVerified(data) {
      if (data.company_name) {
        companyInput.value = data.company_name;
        companyInput.readOnly = true;
        verifiedFromGstin = true;
      }
      const stateNote = data.state ? ` (${data.state})` : '';
      const statusNote =
        data.status && data.status !== 'Active' ? ` — ${data.status}` : '';
      setGstinStatus(root, `Verified${stateNote}${statusNote}`, 'success');
    }

    function fetchGstin(gstin, attempt) {
      const key = gstin;
      if (window.__gstVerifyCache[key]?.company_name) {
        applyVerified(window.__gstVerifyCache[key]);
        return;
      }

      fetch(`/apps/gst-verify?gstin=${encodeURIComponent(gstin)}`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
        .then(async (res) => {
          const text = await res.text();
          let data = null;
          try {
            data = text ? JSON.parse(text) : null;
          } catch (_) {
            data = null;
          }
          if (!data || typeof data.valid === 'undefined') {
            const err = new Error('bad_payload');
            err.retryable = (attempt || 0) < 2;
            throw err;
          }
          return data;
        })
        .then((data) => {
          if (data.valid === false) {
            if (verifiedFromGstin) companyInput.value = '';
            unlockCompany();
            setGstinStatus(
              root,
              data.sandbox
                ? 'Sandbox: use a test GSTIN'
                : data.message || 'GSTIN not found',
              'error',
            );
            return;
          }
          if (data.valid === true) {
            window.__gstVerifyCache[key] = data;
            applyVerified(data);
            return;
          }
          throw Object.assign(new Error('unexpected'), { retryable: true });
        })
        .catch((err) => {
          if (err.retryable && (attempt || 0) < 2) {
            setGstinStatus(root, 'Checking…', 'info');
            setTimeout(() => fetchGstin(gstin, (attempt || 0) + 1), 800);
            return;
          }
          setGstinStatus(root, 'Couldn’t verify — try again', 'error');
        });
    }

    function handleInput() {
      const caret = gstinInput.selectionStart;
      gstinInput.value = gstinInput.value.toUpperCase();
      try {
        gstinInput.setSelectionRange(caret, caret);
      } catch (_) {}

      const value = gstinInput.value.trim();
      if (lookupTimeout) clearTimeout(lookupTimeout);

      if (!value) {
        setGstinStatus(root, '', '');
        if (verifiedFromGstin) {
          companyInput.value = '';
          unlockCompany();
        }
        return;
      }

      if (value.length < 15) {
        setGstinStatus(root, '', '');
        return;
      }

      if (!GSTIN_RE.test(value)) {
        setGstinStatus(root, 'Invalid GSTIN format', 'error');
        return;
      }

      setGstinStatus(root, 'Checking GST records…', 'info');
      lookupTimeout = setTimeout(() => fetchGstin(value, 0), 450);
    }

    gstinInput.addEventListener('input', handleInput);

    // Prefill from account: verify if format looks valid and company empty
    const initial = gstinInput.value.trim().toUpperCase();
    gstinInput.value = initial;
    if (GSTIN_RE.test(initial)) {
      const company = (companyInput.value || '').trim();
      if (!company || company === 'VERIFICATION PENDING') {
        setGstinStatus(root, 'Checking GST records…', 'info');
        fetchGstin(initial, 0);
      } else {
        companyInput.readOnly = true;
        verifiedFromGstin = true;
        setGstinStatus(root, 'Verified', 'success');
      }
    }
  }

  function bindRoot(root) {
    if (!root || root.dataset.sqBound === '1') return;
    root.dataset.sqBound = '1';

    const state = {
      platforms: resolveInitialPlatforms(root),
      options: null,
      source: 'inline',
    };
    const form = $(root, '[data-sq-form]');
    const loggedIn = root.getAttribute('data-logged-in') === 'true';

    bindPlatformCards(root, state);
    bindAccountGate(root, state);

    // Signed-out hard gate: keep platform cards usable, then send to login.
    if (!form || !loggedIn) {
      fetchCatalog().then((catalog) => {
        if (!catalog?.platforms?.length) return;
        state.platforms = catalog.platforms;
        state.options = catalog.options;
        state.source = catalog.source;
      });
      return;
    }

    populatePlatformSelect(root, state.platforms);
    bindAccordion(root);
    bindQuantitySync(root, state);
    bindGstinVerify(root);

    root.querySelectorAll('[data-sq-field]').forEach((field) => {
      field.addEventListener('change', () => {
        if (field.matches('[data-sq-workload]')) {
          syncWorkloadCustomVisibility(root);
          applyWorkloadPreset(root);
        }
        if (field.matches('[data-sq-platform]')) {
          selectPlatform(root, field.value, state.platforms, {
            scroll: false,
            openStep: false,
          }, state);
          saveDraft(root, state);
          return;
        }
        applyCompatibilityRules(root, state);
        saveDraft(root, state);
      });
      field.addEventListener('input', () => {
        updateSummary(root, state.platforms);
        updatePriceStatus(root, state);
        saveDraft(root, state);
      });
    });
    syncWorkloadCustomVisibility(root);

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      submitForm(root, state);
    });

    applyInitialSelection(root, state);

    const draft = readDraft();
    if (draft) applyDraft(root, state, draft);

    root.classList.add('is-sq-ready');
    root.setAttribute('data-sq-ready', 'true');

    fetchCatalog().then((catalog) => {
      if (!catalog?.platforms?.length) return;
      state.platforms = catalog.platforms;
      state.options = catalog.options;
      state.source = catalog.source;
      populatePlatformSelect(root, state.platforms);
      applyInitialSelection(root, state);
      root.dataset.sqCatalogSource = state.source;
      const restored = readDraft();
      if (restored) applyDraft(root, state, restored);
      root.classList.add('is-sq-ready');
    });
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-sq-root]').forEach(bindRoot);
  }

  document.addEventListener('DOMContentLoaded', () => init(document));
  window.BlueberryServerQuote = { init };
})();
